import axiosInstance from './axiosInstance';

export type WorkerFeedbackLifecycleStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type WorkerFeedbackVerdict = 'SUITABLE' | 'RESERVE' | 'NOT_SUITABLE';
export type WorkerFeedbackSource = 'LOCAL' | 'EASYFLEX';
export type WorkerFeedbackVisibility = 'INTERNAL';
export type WorkerFeedbackExternalSyncStatus =
    | 'UNAVAILABLE'
    | 'PENDING'
    | 'SYNCED'
    | 'FAILED';

export interface WorkerFeedbackEntry {
    id: string;
    workerId: string;
    placementId?: string | null;
    placementEndOccurrenceKey?: string | null;
    perspective?: 'WORKER' | 'COMPANY';
    ratingScale?: 5 | 10;
    endReasonCode?: 'COMPLETED' | 'CLIENT_REQUEST' | 'WORKER_REQUEST' | 'MUTUAL_AGREEMENT' | 'OTHER' | null;
    source: WorkerFeedbackSource;
    visibility: WorkerFeedbackVisibility;
    lifecycleStatus: WorkerFeedbackLifecycleStatus;
    externalSyncStatus: WorkerFeedbackExternalSyncStatus;
    verdict?: WorkerFeedbackVerdict | null;
    title?: string | null;
    reviewText?: string | null;
    overallScore?: number | null;
    categoryScores?: Record<string, number> | null;
    organizationName?: string | null;
    easyflexRegistrationNumber?: number | null;
    easyflexPlacementNumber?: number | null;
    createdAt: string;
    updatedAt: string;
    createdBy?: string | null;
    updatedBy?: string | null;
    placement?: {
        id: string;
        title: string;
        status: string;
        startDate: string;
        endDate?: string | null;
        easyflexPlacementNumber?: number | null;
        organizationName?: string | null;
    } | null;
}

export interface WorkerFeedbackSummary {
    feedbackCount: number;
    draftCount: number;
    publishedCount: number;
    archivedCount: number;
    pendingExternalSyncCount: number;
    averageOverallScore: number | null;
    averageCategoryScores: Record<string, number>;
    latestFeedbackAt?: string | null;
}

export interface WorkerFeedbackResponse {
    workerId: string;
    items: WorkerFeedbackEntry[];
    summary: WorkerFeedbackSummary;
    latestEndedPlacement?: {
        id: string;
        easyflexPlacementNumber?: number | null;
        organizationName?: string | null;
        endDate?: string | null;
    } | null;
    latestEndedPlacementFeedbackMissing?: boolean;
    endedPlacementsMissingFeedback?: WorkerFeedbackPlacement[];
    externalSync: {
        supported: boolean;
        status: WorkerFeedbackExternalSyncStatus;
        message: string;
    };
}

export interface WorkerFeedbackPlacement {
    id: string;
    easyflexPlacementNumber?: number | null;
    organizationName?: string | null;
    title?: string | null;
    startDate?: string | null;
    endDate?: string | null;
}

export interface SaveWorkerFeedbackRequest {
    placementId?: string | null;
    verdict?: WorkerFeedbackVerdict;
    reviewText?: string;
    overallScore?: number;
    perspective?: 'WORKER' | 'COMPANY';
    endReasonCode?: 'COMPLETED' | 'CLIENT_REQUEST' | 'WORKER_REQUEST' | 'MUTUAL_AGREEMENT' | 'OTHER';
    lifecycleStatus?: WorkerFeedbackLifecycleStatus;
}

export interface WorkerFeedbackCommandResult {
    commandId: string;
    state: 'PENDING' | 'PROCESSING' | 'SENT' | 'FAILED';
    queued: boolean;
    retryable?: boolean;
    feedbackId?: string;
}

const feedbackPath = (workerId: string) => `/workers/${encodeURIComponent(workerId)}/feedback`;

export async function fetchWorkerFeedback(workerId: string): Promise<WorkerFeedbackResponse> {
    const response = await axiosInstance.get<WorkerFeedbackResponse>(feedbackPath(workerId));
    return response.data;
}

export async function fetchWorkerFeedbackCommandStatus(
    workerId: string,
    commandId: string,
): Promise<WorkerFeedbackCommandResult> {
    const response = await axiosInstance.get<WorkerFeedbackCommandResult>(
        `${feedbackPath(workerId)}/commands/${encodeURIComponent(commandId)}`,
    );
    return response.data;
}

export async function createWorkerFeedback(
    workerId: string,
    payload: SaveWorkerFeedbackRequest,
    commandId: string,
): Promise<WorkerFeedbackCommandResult> {
    const response = await axiosInstance.post<WorkerFeedbackCommandResult>(feedbackPath(workerId), payload, {
        headers: { 'Idempotency-Key': commandId },
    });
    return response.data;
}

export async function updateWorkerFeedback(
    workerId: string,
    feedbackId: string,
    payload: Partial<SaveWorkerFeedbackRequest>,
    commandId: string,
): Promise<WorkerFeedbackCommandResult> {
    const response = await axiosInstance.patch<WorkerFeedbackCommandResult>(
        `${feedbackPath(workerId)}/${encodeURIComponent(feedbackId)}`,
        payload,
        { headers: { 'Idempotency-Key': commandId } },
    );
    return response.data;
}

export async function publishWorkerFeedback(
    workerId: string,
    feedbackId: string,
    commandId: string,
): Promise<WorkerFeedbackCommandResult> {
    const response = await axiosInstance.post<WorkerFeedbackCommandResult>(
        `${feedbackPath(workerId)}/${encodeURIComponent(feedbackId)}/publish`,
        {},
        { headers: { 'Idempotency-Key': commandId } },
    );
    return response.data;
}
